import { useState } from "react";

export interface MarkBand {
  label: string;
  min: number; // 0-100 inclusive
  colour: string;
}

interface Props {
  bands: MarkBand[];
  onChange: (bands: MarkBand[]) => void;
}

function clamp(value: number, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value));
}

export default function MarkingScaleEditor({ bands, onChange }: Props) {
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  // Sort by min descending to render correctly
  const sorted = [...bands].sort((a, b) => b.min - a.min);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dragIdx === null) return;
    const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
    const pct = clamp(((e.clientX - rect.left) / rect.width) * 100);
    // Update band min for dragged index (except first & last boundaries)
    onChange(
      bands.map((b, idx) => (idx === dragIdx ? { ...b, min: Math.round(pct) } : b)),
    );
  };

  const handleStop = () => setDragIdx(null);

  return (
    <div className="space-y-3">
      {/* Visual bar */}
      <div
        className="relative h-8 w-full rounded-md border border-gray-300"
        onMouseMove={handleMouseMove}
        onMouseUp={handleStop}
        onMouseLeave={handleStop}
      >
        {sorted.map((band, idx) => {
          const nextMin = idx === 0 ? 100 : sorted[idx - 1].min;
          const width = nextMin - band.min;
          return (
            <div
              key={idx}
              style={{
                left: `${band.min}%`,
                width: `${width}%`,
                background: band.colour,
              }}
              className="absolute top-0 h-full text-xs flex items-center justify-center text-white"
            >
              {band.label}
              {idx !== sorted.length - 1 && (
                <span
                  onMouseDown={() => setDragIdx(bands.indexOf(band))}
                  className="absolute -left-1.5 h-full w-3 cursor-ew-resize"
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Editable table */}
      <table className="min-w-full text-xs">
        <thead>
          <tr className="text-left text-gray-600">
            <th>Label</th>
            <th>Min %</th>
            <th>Colour</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {bands
            .slice()
            .sort((a, b) => b.min - a.min)
            .map((b, i) => (
              <tr key={i} className="border-b border-gray-100">
                <td>
                  <input
                    type="text"
                    value={b.label}
                    onChange={(e) =>
                      onChange(
                        bands.map((x, idx) =>
                          idx === i ? { ...x, label: e.target.value } : x,
                        ),
                      )
                    }
                    className="w-20 rounded-md border-gray-200 shadow-sm"
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value={b.min}
                    min={0}
                    max={100}
                    onChange={(e) =>
                      onChange(
                        bands.map((x, idx) =>
                          idx === i ? { ...x, min: Number(e.target.value) } : x,
                        ),
                      )
                    }
                    className="w-20 rounded-md border-gray-200 shadow-sm"
                  />
                </td>
                <td>
                  <input
                    type="color"
                    value={b.colour}
                    onChange={(e) =>
                      onChange(
                        bands.map((x, idx) =>
                          idx === i ? { ...x, colour: e.target.value } : x,
                        ),
                      )
                    }
                  />
                </td>
                <td>
                  {bands.length > 2 && (
                    <button
                      onClick={() =>
                        onChange(bands.filter((_, idx) => idx !== i))
                      }
                      className="text-red-600 hover:underline"
                    >
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
        </tbody>
      </table>

      <button
        onClick={() =>
          onChange([
            ...bands,
            {
              label: "New",
              min: 0,
              colour: "#a3a3a3",
            },
          ])
        }
        className="rounded-md border border-dashed border-primary px-2 py-1 text-xs text-primary hover:bg-primary/10"
      >
        + Add band
      </button>
    </div>
  );
}
